export function createIdempotencyKey(prefix = "web"): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

function stableSerialize(value: unknown): string {
  if (value === undefined) return "undefined";
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value))
    return `[${value.map((item) => stableSerialize(item)).join(",")}]`;
  return `{${Object.entries(value as Record<string, unknown>)
    .filter(([, item]) => item !== undefined)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, item]) => `${JSON.stringify(key)}:${stableSerialize(item)}`)
    .join(",")}}`;
}

interface ActionEntry<T = unknown> {
  fingerprint: string;
  key: string;
  pending?: Promise<T>;
}

/** Keeps one key for a logical action until it succeeds. */
export class IdempotencyManager {
  private readonly entries = new Map<string, ActionEntry>();

  constructor(
    private readonly keyFactory: (prefix: string) => string =
      createIdempotencyKey,
  ) {}

  run<T>(
    action: string,
    organizationId: string,
    payload: unknown,
    operation: (key: string) => Promise<T>,
  ): Promise<T> {
    if (!organizationId) return Promise.reject(new Error("请先选择企业"));
    const scope = `${organizationId}:${action}`;
    const fingerprint = stableSerialize(payload);
    const current = this.entries.get(scope) as ActionEntry<T> | undefined;
    if (current?.pending && current.fingerprint === fingerprint)
      return current.pending;

    const entry: ActionEntry<T> =
      current && current.fingerprint === fingerprint
        ? current
        : {
            fingerprint,
            key: this.keyFactory(action.replace(/[^a-z0-9-]/gi, "-")),
          };
    const pending = operation(entry.key)
      .then((result) => {
        if (this.entries.get(scope) === entry) this.entries.delete(scope);
        return result;
      })
      .catch((error: unknown) => {
        entry.pending = undefined;
        throw error;
      });
    entry.pending = pending;
    this.entries.set(scope, entry);
    return pending;
  }

  clearOrganization(organizationId: string): void {
    for (const scope of this.entries.keys())
      if (scope.startsWith(`${organizationId}:`)) this.entries.delete(scope);
  }

  clearAll(): void {
    this.entries.clear();
  }
}

export const idempotency = new IdempotencyManager();
